import { fetchCtaContent, updateCtaContent } from './ctaApi';
import {
	CTA_BANNER_IDS,
	CtaBannerId,
	CtaContent,
	DEFAULT_CTA_CONTENT,
} from './types';

const BANNER_DISMISSED_SESSION_PREFIX = 'ps_cta_banner_dismissed_version_';
const BANNER_PENDING_LOGIN_PREFIX = 'ps_cta_banner_pending_login_';
/** The primary banner keeps the original, unsuffixed keys so existing sessions keep working. */
const BANNER_KEY_SUFFIX: Record<CtaBannerId, string> = {
	primary: '',
	secondary: 'two_',
};
const POPUP_PENDING_LOGIN_PREFIX = 'ps_cta_popup_pending_login_';
const POPUP_DISMISSED_LOCAL_PREFIX = 'ps_cta_popup_dismissed_version_';

type CtaListener = () => void;
const listeners = new Set<CtaListener>();

let cachedContent: CtaContent = { ...DEFAULT_CTA_CONTENT };
let loadPromise: Promise<CtaContent> | null = null;
let contentLoaded = false;

export const isCtaContentLoaded = (): boolean => contentLoaded;

export const subscribeToCtaContent = (listener: CtaListener): (() => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

const notifyListeners = () => {
	listeners.forEach((listener) => listener());
};

const setCachedContent = (content: CtaContent) => {
	cachedContent = { ...DEFAULT_CTA_CONTENT, ...content };
};

export const getCtaContent = (): CtaContent => ({ ...cachedContent });

export const loadCtaContentFromApi = async (force = false): Promise<CtaContent> => {
	if (force) {
		loadPromise = null;
		contentLoaded = false;
	}

	if (loadPromise) return loadPromise;

	loadPromise = fetchCtaContent()
		.then((content) => {
			setCachedContent(content);
			contentLoaded = true;
			notifyListeners();
			return content;
		})
		.catch(() => cachedContent)
		.finally(() => {
			loadPromise = null;
		});

	return loadPromise;
};

export const saveCtaContentUpdates = async (updates: Partial<CtaContent>): Promise<CtaContent> => {
	const content = await updateCtaContent(updates);
	setCachedContent(content);
	notifyListeners();
	return content;
};

const bannerPendingLoginKey = (userId: number | string, bannerId: CtaBannerId) =>
	`${BANNER_PENDING_LOGIN_PREFIX}${BANNER_KEY_SUFFIX[bannerId]}${userId}`;

const bannerDismissedKey = (userId: number | string, bannerId: CtaBannerId) =>
	`${BANNER_DISMISSED_SESSION_PREFIX}${BANNER_KEY_SUFFIX[bannerId]}${userId}`;

export const setPendingLoginBanner = (userId: number | string) => {
	CTA_BANNER_IDS.forEach((bannerId) => {
		sessionStorage.setItem(bannerPendingLoginKey(userId, bannerId), '1');
		sessionStorage.removeItem(bannerDismissedKey(userId, bannerId));
	});
	notifyListeners();
};

export const getPendingLoginBanner = (
	userId: number | string,
	bannerId: CtaBannerId,
): boolean => sessionStorage.getItem(bannerPendingLoginKey(userId, bannerId)) === '1';

export const getBannerDismissedVersion = (
	userId: number | string,
	bannerId: CtaBannerId,
): number => {
	const value = sessionStorage.getItem(bannerDismissedKey(userId, bannerId));
	return value ? Number(value) : 0;
};

export const dismissBanner = (
	userId: number | string,
	version: number,
	bannerId: CtaBannerId,
) => {
	sessionStorage.setItem(bannerDismissedKey(userId, bannerId), String(version));
	sessionStorage.removeItem(bannerPendingLoginKey(userId, bannerId));
	notifyListeners();
};

export const clearBannerSessionState = (userId: number | string) => {
	CTA_BANNER_IDS.forEach((bannerId) => {
		sessionStorage.removeItem(bannerPendingLoginKey(userId, bannerId));
		sessionStorage.removeItem(bannerDismissedKey(userId, bannerId));
	});
	notifyListeners();
};

export const setPendingLoginPopup = (userId: number | string, version: number) => {
	sessionStorage.setItem(`${POPUP_PENDING_LOGIN_PREFIX}${userId}`, String(version));
	notifyListeners();
};

export const getPendingLoginPopupVersion = (userId: number | string): number | null => {
	const value = sessionStorage.getItem(`${POPUP_PENDING_LOGIN_PREFIX}${userId}`);
	return value ? Number(value) : null;
};

export const clearPendingLoginPopup = (userId: number | string) => {
	sessionStorage.removeItem(`${POPUP_PENDING_LOGIN_PREFIX}${userId}`);
};

export const parsePopupDismissedVersion = (value: string | undefined | null): number => {
	if (!value) return 0;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

export const getPopupDismissedVersionLocal = (userId: number | string): number => {
	const value = localStorage.getItem(`${POPUP_DISMISSED_LOCAL_PREFIX}${userId}`);
	return parsePopupDismissedVersion(value);
};

export const setPopupDismissedVersionLocal = (userId: number | string, version: number) => {
	localStorage.setItem(`${POPUP_DISMISSED_LOCAL_PREFIX}${userId}`, String(version));
};

export const syncUserDataCtaDismissedVersion = (version: number) => {
	try {
		const stored = localStorage.getItem('user_data');
		if (!stored) return;
		const userObj = JSON.parse(stored);
		if (userObj.user_data) {
			userObj.user_data.cta_popup_dismissed_version = String(version);
			localStorage.setItem('user_data', JSON.stringify(userObj));
		}
	} catch {
		// ignore malformed storage
	}
};

export const getEffectivePopupDismissedVersion = (
	userId: number | string | undefined,
	serverVersion: string | undefined | null,
): number => {
	const localVersion = userId ? getPopupDismissedVersionLocal(userId) : 0;
	return Math.max(parsePopupDismissedVersion(serverVersion), localVersion);
};
